import React from "react";
import { Lock, ArrowRight, ShieldCheck, Mail, ExternalLink } from "lucide-react";
import { openSubscribeGate } from "../../lib/subscription";
import { SUPPORT_EMAIL } from "../../lib/constants";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PreviewExpiredLock — full-screen hard-gate shown inside the mobile app
 * shell once the visitor's 7-day free preview has expired and they have
 * not chosen a plan. Blocks all interaction with the app content behind it
 * (Home / AI / Range / Trip / More) until the visitor picks a plan or the
 * account owner enables owner-mode from /admin.
 *
 * Design notes:
 *  - Sits absolutely inside the phone-frame (not the whole viewport) so on
 *    desktop it still respects the phone bezel and the admin's marketing
 *    chrome remains visible.
 *  - The tab bar and status bar deliberately keep rendering behind the
 *    lock, so the visitor can see what they're unlocking.
 */
export const PreviewExpiredLock = () => (
  <div
    data-testid="preview-expired-lock"
    className="absolute inset-0 z-40 flex flex-col items-center justify-center px-6 text-center"
    style={{
      background:
        "linear-gradient(180deg, rgba(0,0,0,0.86) 0%, rgba(0,0,0,0.95) 100%)",
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
    }}
    role="dialog"
    aria-modal="true"
    aria-label="Preview expired — choose a plan to continue"
  >
    <div
      className="w-14 h-14 rounded-full flex items-center justify-center mb-5"
      style={{
        background: "rgba(255,107,87,0.14)",
        border: "1.5px solid rgba(255,107,87,0.5)",
      }}
    >
      <Lock className="w-6 h-6" style={{ color: "#FF6B57" }} strokeWidth={2.4} />
    </div>

    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF6B57] mb-2">
      Free preview ended
    </div>
    <h2
      className="font-display text-2xl text-white font-semibold leading-tight max-w-[280px]"
      data-testid="preview-lock-title"
    >
      Your 7-day preview has ended.
    </h2>
    <p className="mt-3 text-[13px] text-zinc-400 leading-relaxed max-w-[280px]">
      Pick a plan to keep monitoring your battery, warranty countdown, and AI
      recommendations. Cancel anytime by email.
    </p>

    <button
      onClick={() => openSubscribeGate("preview-lock-overlay")}
      data-testid="preview-lock-choose-plan"
      className="mt-6 w-full max-w-[260px] brand-gradient text-black font-semibold rounded-2xl px-5 py-3.5 text-sm inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity glow-green"
    >
      Choose plan
      <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
    </button>

    <div className="mt-3 text-[11px] font-mono text-zinc-500 flex items-center gap-1.5">
      <ExternalLink className="w-3 h-3" strokeWidth={2} />
      Secure checkout via Stripe
    </div>

    {/* Owner escape-hatch — the account owner can sign in with Tesla and
        (if their email matches the OWNER_EMAILS allowlist) the backend
        auto-flips is_owner=true, which lifts this lock on the next tick.
        Deliberately quiet so it reads as a "not you" fallback, not a
        primary CTA that visitors would click by mistake. */}
    <a
      href={`${API}/api/tesla/authorize?return_to=/app`}
      data-testid="preview-lock-owner-signin"
      className="mt-5 text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1.5"
    >
      <ShieldCheck className="w-3 h-3" strokeWidth={2} />
      Account owner? Sign in with Tesla →
    </a>

    <div className="mt-6 flex items-center gap-3 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
      <span className="inline-flex items-center gap-1">
        <ShieldCheck className="w-3 h-3" strokeWidth={1.8} />
        Cancel anytime
      </span>
      <span className="text-white/10">·</span>
      <a
        href={`mailto:${SUPPORT_EMAIL}?subject=EVIQ%20support`}
        className="inline-flex items-center gap-1 hover:text-white transition-colors"
        data-testid="preview-lock-support-link"
      >
        <Mail className="w-3 h-3" strokeWidth={1.8} />
        {SUPPORT_EMAIL}
      </a>
    </div>
  </div>
);
